import React from "react";
import { cn } from "@/lib/utils";
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  Tooltip,
  ResponsiveContainer,
  ReferenceLine,
} from "recharts";

interface PensionData {
  staatlich?: number;
  betrieblich?: number;
  privat?: number;
  bedarf?: number;
}

interface PensionGapChartProps {
  data: PensionData;
  animated?: boolean;
  className?: string;
}

export function PensionGapChart({
  data,
  animated = true,
  className,
}: PensionGapChartProps) {
  const { staatlich = 0, betrieblich = 0, privat = 0, bedarf = 2500 } = data;
  const total = staatlich + betrieblich + privat;
  const gap = Math.max(0, bedarf - total);
  const gapPercentage = bedarf > 0 ? Math.round((gap / bedarf) * 100) : 0;

  const chartData = [
    { name: "1. Säule", value: staatlich, label: "Staatliche Pension" },
    { name: "2. Säule", value: betrieblich, label: "Betriebliche Vorsorge" },
    { name: "3. Säule", value: privat, label: "Private Vorsorge" },
  ];

  const formatCurrency = (value: number) => {
    return new Intl.NumberFormat('de-AT', {
      style: 'currency',
      currency: 'EUR',
      maximumFractionDigits: 0,
    }).format(value);
  };

  return (
    <div className={cn("space-y-6", className)}>
      <div className="text-center">
        <h3 className="font-semibold text-lg">Das 3-Säulen-System</h3>
        <p className="text-sm text-muted-foreground">
          Ihre Altersvorsorge im Überblick
        </p>
      </div>

      {/* Three Pillars Visualization */}
      <div className="flex justify-center items-end gap-4 h-48 pt-4">
        {chartData.map((pillar, index) => {
          const maxHeight = 160;
          const height = bedarf > 0 ? (pillar.value / bedarf) * maxHeight : 0;
          const colors = [
            "from-blue-500 to-blue-600",
            "from-emerald-500 to-emerald-600",
            "from-primary to-primary/80",
          ];

          return (
            <div
              key={pillar.name}
              className={cn(
                "flex flex-col items-center gap-2",
                animated && "animate-in fade-in slide-in-from-bottom-4"
              )}
              style={animated ? { animationDelay: `${index * 150}ms` } : undefined}
            >
              <span className="text-sm font-medium">{formatCurrency(pillar.value)}</span>
              <div
                className={cn(
                  "w-20 rounded-t-lg bg-gradient-to-t transition-all duration-700",
                  colors[index]
                )}
                style={{ height: Math.max(height, 20) }}
              />
              <div className="text-center">
                <span className="block text-xs font-semibold">{pillar.name}</span>
                <span className="block text-xs text-muted-foreground">{pillar.label}</span>
              </div>
            </div>
          );
        })}
      </div>

      {/* Gap Indicator */}
      <div className={cn(
        "p-4 rounded-lg",
        gap > 0 ? "bg-destructive/10" : "bg-emerald-500/10"
      )}>
        <div className="flex items-center justify-between mb-2">
          <span className="font-medium">Monatlicher Bedarf:</span>
          <span className="font-semibold">{formatCurrency(bedarf)}</span>
        </div>
        <div className="flex items-center justify-between mb-2">
          <span className="text-muted-foreground">Aktuelle Vorsorge:</span>
          <span>{formatCurrency(total)}</span>
        </div>
        <div className="border-t border-current/20 pt-2 mt-2">
          <div className="flex items-center justify-between">
            <span className={cn(
              "font-semibold",
              gap > 0 ? "text-destructive" : "text-emerald-600"
            )}>
              {gap > 0 ? "Versorgungslücke:" : "Überschuss:"}
            </span>
            <span className={cn(
              "text-xl font-bold",
              gap > 0 ? "text-destructive" : "text-emerald-600"
            )}>
              {formatCurrency(gap > 0 ? gap : total - bedarf)}
            </span>
          </div>
        </div>
      </div>

      {gap > 0 && (
        <p className="text-center text-sm text-muted-foreground">
          Um Ihren Lebensstandard zu halten, fehlen Ihnen noch{" "}
          <span className="font-semibold text-destructive">{gapPercentage}%</span> Ihrer
          benötigten Altersvorsorge.
        </p>
      )}
    </div>
  );
}
